<template>
  <div
    :id="rootId"
    ref="videoWrapper"
    class="relative w-full h-auto aspect-video overflow-hidden isolate bg-black"
    :class="{ 'rounded-xl': props.rounded }"
  >
    <BoosterYoutube
      :url="props.url"
      :title="props.title"
      :options="props.options"
      :host="props.host"
      :mute="props.mute"
      :autoplay="props.autoplay"
      @ready="onReady"
      @playing="onPlaying"
    />
  </div>
</template>

<script setup lang="ts">
import BoosterYoutube from '#booster/components/BoosterYoutube.vue'
import type { Player } from 'youtube'

type TOptions = {
  [key: string]: string | number | boolean
}

type TProps = {
  url?: string
  title?: string
  host?: string
  options?: TOptions
  mute?: boolean
  autoplay?: boolean
  rounded?: boolean
}

const props = withDefaults(defineProps<TProps>(), {
  url: '',
  title: 'YouTube Video',
  host: 'https://www.youtube.com',
  options: () => ({}),
  mute: false,
  autoplay: true,
})

const rootId = useId()

type TEmit = {
  ready: [data: { iframe: HTMLIFrameElement; player: { value: Player } }]
  playing: [isPlaying: boolean]
}

const emit = defineEmits<TEmit>()

const onPlaying = (isPlaying: boolean) => {
  emit('playing', isPlaying)
}

type TExpose = {
  rootId: string
}

defineExpose<TExpose>({
  rootId,
})

const onReady = async (e: {
  iframe: HTMLIFrameElement
  player: { value: Player }
}) => {
  if (!e || !e.player || !e.player.value) return

  await nextTick()

  e.player.value.unMute()

  emit('ready', {
    iframe: e.iframe,
    player: e.player,
  })
}
</script>

<style scoped>
:deep(iframe),
:deep(img) {
  width: 100% !important;
  height: 100% !important;
  border-radius: inherit;
  object-fit: cover;
}
</style>
